import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { OnboardingStep } from '../OnboardingFlow';

interface StepProgress {
  completed: boolean;
  timestamp: number;
  data?: Record<string, unknown>;
}

interface LoadingState {
  isLoading: boolean;
  message: string;
  itemId?: string;
}

interface ErrorState {
  message: string;
  code?: string;
  step: OnboardingStep;
  component?: string;
  action?: string;
  timestamp: number;
  retryCount: number;
  canRetry: boolean;
}

interface OnboardingContextValue {
  // Current state
  currentStep: OnboardingStep;
  progress: Record<OnboardingStep, StepProgress>;
  loading: LoadingState;
  error: ErrorState | null;
  footerContent: React.ReactNode | null;

  // Navigation
  goToStep: (step: OnboardingStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;

  // Progress management
  markStepComplete: (
    step: OnboardingStep,
    data?: Record<string, unknown>,
  ) => void;
  markStepIncomplete: (step: OnboardingStep) => void;

  // Error handling
  setError: (error: Omit<ErrorState, 'timestamp' | 'retryCount'>) => void;
  clearError: () => void;
  retryCurrentStep: () => void;

  // Resume capability
  lastCompletedStep: OnboardingStep | null;
  canResume: boolean;

  // Footer content management
  setFooterContent: (content: React.ReactNode | null) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface OnboardingProviderProps {
  children: React.ReactNode;
}

const STEP_ORDER: OnboardingStep[] = ['landing', 'tools-setup', 'completion'];

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const [state, setState] = React.useState<{
    currentStep: OnboardingStep;
    progress: Record<OnboardingStep, StepProgress>;
    loading: LoadingState;
    error: ErrorState | null;
    footerContent: React.ReactNode | null;
  }>({
    currentStep: 'landing',
    progress: {
      landing: { completed: false, timestamp: 0 },
      'tools-setup': { completed: false, timestamp: 0 },
      completion: { completed: false, timestamp: 0 },
    },
    loading: { isLoading: false, message: '' },
    error: null,
    footerContent: null,
  });

  const goToStep = useCallback((step: OnboardingStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(state.currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      goToStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [state.currentStep, goToStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(state.currentStep);
    if (currentIndex > 0) {
      goToStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [state.currentStep, goToStep]);

  const markStepComplete = useCallback(
    (step: OnboardingStep, data?: Record<string, unknown>) => {
      setState((prev) => ({
        ...prev,
        progress: {
          ...prev.progress,
          [step]: {
            completed: true,
            timestamp: Date.now(),
            data,
          },
        },
      }));
    },
    [],
  );

  const markStepIncomplete = useCallback((step: OnboardingStep) => {
    setState((prev) => ({
      ...prev,
      progress: {
        ...prev.progress,
        [step]: {
          completed: false,
          timestamp: Date.now(),
        },
      },
    }));
  }, []);

  const setError = useCallback(
    (error: Omit<ErrorState, 'timestamp' | 'retryCount'>) => {
      setState((prev) => ({
        ...prev,
        error: {
          ...error,
          timestamp: Date.now(),
          retryCount: prev.error?.retryCount ?? 0,
        },
      }));
    },
    [],
  );

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const retryCurrentStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: prev.error
        ? { ...prev.error, retryCount: prev.error.retryCount + 1 }
        : null,
    }));
  }, []);

  const lastCompletedStep = useMemo(() => {
    const completed = Object.entries(state.progress)
      .filter(([, progress]) => progress.completed)
      .map(([step]) => step as OnboardingStep);
    return completed.length > 0 ? completed[completed.length - 1] : null;
  }, [state.progress]);

  const canResume = useMemo(() => {
    return lastCompletedStep !== null && lastCompletedStep !== 'completion';
  }, [lastCompletedStep]);

  const setFooterContent = useCallback((content: React.ReactNode | null) => {
    setState((prev) => ({ ...prev, footerContent: content }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      markStepComplete,
      markStepIncomplete,
      setError,
      clearError,
      retryCurrentStep,
      lastCompletedStep,
      canResume,
      setFooterContent,
    }),
    [
      state,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      markStepComplete,
      markStepIncomplete,
      setError,
      clearError,
      retryCurrentStep,
      lastCompletedStep,
      canResume,
      setFooterContent,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export type { OnboardingContextValue, StepProgress, LoadingState, ErrorState };
